import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import IdentityVerificationModal from '../../components/verification/IdentityVerificationModal';
import TopUpModal from '../../components/wallet/TopUpModal';
import PaymentMethodForm from '../../components/payment/PaymentMethodForm';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { isSecondaryVerificationError } from '../../utils/verificationGate';
import paymentService from '../../services/paymentService';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { Wallet as WalletIcon, ArrowUpRight, ArrowDownLeft, CreditCard, Smartphone, Building, CheckCircle, Clock, AlertCircle, X, Trash2, AlertTriangle, Plus, Gift, Shield } from 'lucide-react';
export const WithdrawModal = ({
  availableBalance,
  minWithdrawal,
  currency = 'GHS',
  onClose,
  onSubmit,
  loading: parentLoading,
  paymentMethods = [],
  onMethodAdded,
}) => {
  const { t } = useTranslation();
  const usablePaymentMethods = paymentMethods.filter(pm => pm.type !== 'card' && pm.verificationStatus !== 'rejected');
  const defaultMethod = usablePaymentMethods.find(pm => pm.isDefault) || usablePaymentMethods[0];
  const [amount, setAmount] = useState('');
  const [useSavedMethod, setUseSavedMethod] = useState(!!defaultMethod);
  const [selectedMethodId, setSelectedMethodId] = useState(defaultMethod?.id || defaultMethod?._id);

  // The minimum comes from the server (GET /wallet -> withdrawalLimits.minimum).
  // Until it has loaded, no amount is considered valid.
  const hasMinimum = typeof minWithdrawal === 'number' && Number.isFinite(minWithdrawal) && minWithdrawal > 0;
  const parsedAmount = parseFloat(amount);
  const isAmountValid = hasMinimum && !isNaN(parsedAmount) && parsedAmount >= minWithdrawal && parsedAmount <= availableBalance;

  const handleSavedSubmit = (e) => {
    e.preventDefault();
    if (!isAmountValid) {
      if (!hasMinimum) return;
      if (!amount || isNaN(parsedAmount) || parsedAmount < minWithdrawal) {
        toast.error(t('dashboard.wallet.minimum_amount', { defaultValue: 'Minimum: GHS {{amount}}', amount: minWithdrawal }));
      } else if (parsedAmount > availableBalance) {
        toast.error('Amount exceeds available balance');
      }
      return;
    }

    const method = usablePaymentMethods.find(m => (m.id || m._id) === selectedMethodId);
    if (!method) return;
    onSubmit(amount, { methodId: method.id || method._id });
  };

  const handleNewMethodVerified = ({ methodId, updatedMethods }) => {
    if (onMethodAdded) onMethodAdded(updatedMethods);
    if (!isAmountValid) {
      setSelectedMethodId(methodId);
      setUseSavedMethod(true);
      return;
    }
    onSubmit(amount, { methodId });
  };

  const handleNewMethodManualSubmitted = (updatedMethods) => {
    if (onMethodAdded) onMethodAdded(updatedMethods);
    setUseSavedMethod(true);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <div>
            <h3 className="text-xl font-semibold">{t('wallet.withdraw_funds')}</h3>
            <p className="text-sm text-gray-600 mt-1">
              {t('dashboard.wallet.available_gh', { defaultValue: 'Available: GH₵' })}
              {availableBalance.toFixed(2)}
            </p>
          </div>
          <button onClick={onClose} disabled={parentLoading} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {hasMinimum && availableBalance < minWithdrawal ? (
            <div className="bg-yellow-50 p-4 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  {t('dashboard.wallet.insufficient_balance_title', { defaultValue: 'Insufficient balance' })}
                </p>
                <p className="text-sm text-yellow-700 mt-0.5">
                  {t('dashboard.wallet.minimum_withdrawal_notice', {
                    defaultValue: 'Minimum withdrawal is {{currency}} {{amount}}. Your current available balance is {{currency}} {{balance}}.',
                    currency,
                    amount: minWithdrawal,
                    balance: availableBalance.toFixed(2),
                  })}
                </p>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.wallet.amount_ghs', { defaultValue: 'Amount (GHS)' })}
              </label>
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                min={hasMinimum ? minWithdrawal : undefined}
                max={availableBalance}
                step="0.01"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('dashboard.wallet.enter_amount', { defaultValue: 'Enter amount' })}
                required
              />
              {hasMinimum && (
                <p className="text-xs text-gray-500 mt-1">
                  {t('dashboard.wallet.minimum_amount', { defaultValue: 'Minimum: GHS {{amount}}', amount: minWithdrawal })}
                </p>
              )}
            </div>
          )}

          {usablePaymentMethods.length > 0 && (
            <div className="flex space-x-4 mb-4">
              <button
                type="button"
                onClick={() => setUseSavedMethod(true)}
                className={`flex-1 py-2 text-sm font-medium rounded-lg border ${useSavedMethod ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >
                {t('wallet.saved_methods', 'Saved Methods')}
              </button>
              <button
                type="button"
                onClick={() => setUseSavedMethod(false)}
                className={`flex-1 py-2 text-sm font-medium rounded-lg border ${!useSavedMethod ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >
                {t('wallet.new_method', 'New Method')}
              </button>
            </div>
          )}

          {useSavedMethod && usablePaymentMethods.length > 0 ? (
            <form onSubmit={handleSavedSubmit} className="space-y-4">
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">{t('wallet.select_saved_method', 'Select Saved Method')}</label>
                {usablePaymentMethods.map(method => {
                  const methodId = method.id || method._id;
                  const isSelected = selectedMethodId === methodId;
                  return (
                    <div
                      key={methodId}
                      onClick={() => setSelectedMethodId(methodId)}
                      className={`p-3 border rounded-lg cursor-pointer flex items-center justify-between ${isSelected ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-blue-300'}`}
                    >
                      <div className="flex items-center">
                        <div className={`w-4 h-4 rounded-full border mr-3 flex items-center justify-center ${isSelected ? 'border-blue-600' : 'border-gray-400'}`}>
                          {isSelected && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 capitalize">{method.details?.bank || method.provider}</p>
                          <p className="text-sm text-gray-500">{method.identifier}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {method.isDefault && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                            {t('wallet.default_label', 'Default')}
                          </span>
                        )}
                        {method.verificationStatus === 'pending' && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-medium">
                            {t('wallet.pending_review', 'Pending Review')}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition text-sm"
                  disabled={parentLoading}
                >
                  {t('dashboard.wallet.cancel', { defaultValue: 'Cancel' })}
                </button>
                <button
                  type="submit"
                  disabled={
                    parentLoading ||
                    !isAmountValid ||
                    !selectedMethodId
                  }
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm font-medium"
                >
                  {parentLoading ? t('wallet.processing', 'Processing...') : t('wallet.withdraw', 'Withdraw')}
                </button>
              </div>
            </form>
          ) : !isAmountValid ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center text-sm text-blue-800">
              <p className="font-medium">
                {hasMinimum && availableBalance < minWithdrawal
                  ? t('dashboard.wallet.insufficient_balance_title', { defaultValue: 'Insufficient balance' })
                  : t('dashboard.wallet.enter_valid_amount_first', {
                      defaultValue: 'Please enter a valid withdrawal amount above before adding a payment method.',
                    })}
              </p>
            </div>
          ) : (
            <PaymentMethodForm
              isInline={true}
              showSuccessStep={false}
              reloadOnSuccess={false}
              onVerified={handleNewMethodVerified}
              onManualSubmitted={handleNewMethodManualSubmitted}
            />
          )}
        </div>
      </div>
    </div>
  );
};
const AddFundsModal = ({
  isOpen,
  onClose,
  onAddFunds,
  loading,
  userCountry,
  manualEnabled
}) => {
  const { t } = useTranslation();
  const {
    user
  } = useAuth();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('auto');
  const [manualDetails, setManualDetails] = useState({
    reference: '',
    date: ''
  });
  if (!isOpen) return null;
  const handleSubmit = e => {
    e.preventDefault();
    onAddFunds(amount, method, method === 'manual' ? manualDetails : null);
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X className="w-6 h-6" />
        </button>
        <h3 className="text-xl font-bold text-gray-900 mb-4">{t('wallet.add_funds_title')}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("dashboard.wallet.amount_ghs", {
              defaultValue: "Amount (GHS)"
            })}</label>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="w-full px-4 py-2 border rounded-lg" required min="1" />
          </div>



          {manualEnabled && <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('wallet.method_label')}</label>
              <select value={method} onChange={e => setMethod(e.target.value)} className="w-full px-4 py-2 border rounded-lg">
                <option value="auto">{t("dashboard.wallet.instant_payment_online", {
                defaultValue: "Instant Payment (Online)"
              })}</option>
                <option value="manual">{t('wallet.manual_bank_transfer')}</option>
              </select>
            </div>}
          {method === 'manual' && <>
              <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                {t("dashboard.wallet.please_transfer_to", {
              defaultValue: "Please transfer to:"
            })} <br />
                {t("dashboard.wallet.bank_gcb_bank", {
              defaultValue: "Bank: GCB Bank"
            })} <br />
                {t("dashboard.wallet.account_1234567890", {
              defaultValue: "Account: 1234567890"
            })} <br />
                {t("dashboard.wallet.name_gigship_inc", {
              defaultValue: "Name: GigShip Inc"
            })}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('wallet.transaction_reference')}</label>
                <input type="text" value={manualDetails.reference} onChange={e => setManualDetails({
              ...manualDetails,
              reference: e.target.value
            })} className="w-full px-4 py-2 border rounded-lg" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('wallet.date_label')}</label>
                <input type="date" value={manualDetails.date} onChange={e => setManualDetails({
              ...manualDetails,
              date: e.target.value
            })} className="w-full px-4 py-2 border rounded-lg" required />
              </div>
            </>}
          <button type="submit" disabled={loading} className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {loading ? 'Processing...' : 'Proceed'}
          </button>
        </form>
      </div>
    </div>;
};
const Wallet = () => {
  const { t } = useTranslation();
  const {
    user,
    loadUser
  } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddFunds, setShowAddFunds] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [showIdVerifyModal, setShowIdVerifyModal] = useState(false);
  const [filter, setFilter] = useState('all');

  // Referral balance state
  const [referralAction, setReferralAction] = useState(null); // 'withdraw' | 'transfer' | null
  const [referralAmount, setReferralAmount] = useState('');
  const [referralLoading, setReferralLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [manualEnabled, setManualEnabled] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [setDefaultLoading, setSetDefaultLoading] = useState(null); // methodId being set

  // Check if user has secondary verification
  const isSecondaryVerified = user?.verificationLevel === 'secondary' || user?.verificationLevel === 'full';
  useEffect(() => {
    loadTransactions();
  }, [filter]);

  const fetchPaymentMethods = async () => {
    try {
      const res = await paymentService.getPaymentMethods();
      if (res.success) setPaymentMethods(res.data || []);
    } catch (err) {
      console.error('Failed to fetch payment methods', err);
    }
  };

  const handleSetDefault = async (methodId) => {
    setSetDefaultLoading(methodId);
    try {
      const res = await paymentService.setDefaultPaymentMethod(methodId);
      if (res.success) {
        setPaymentMethods(res.data || []);
        toast.success('Default payment method updated');
      } else {
        toast.error(res.message || 'Failed to update default method');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update default method');
    } finally {
      setSetDefaultLoading(null);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);
  const loadWallet = useCallback(async () => {
    try {
      const res = await api.get('/wallet');
      if (res.data?.success && res.data.wallet) {
        // Prisma Decimals are serialised as strings, but the UI calls .toFixed() on these.
        const toNum = v => {
          const n = Number(v);
          return Number.isFinite(n) ? n : 0;
        };
        const w = res.data.wallet;
        setWallet({
          ...w,
          availableBalance: toNum(w.availableBalance),
          pendingBalance: toNum(w.pendingBalance),
          referralBalance: toNum(w.referralBalance),
          unpaidCommissions: toNum(w.unpaidCommissions),
          pendingClearance: (Array.isArray(w.pendingClearance) ? w.pendingClearance : []).map(p => ({ ...p, amount: toNum(p.amount) })),
        });
      }
    } catch (err) {
      console.error('Failed to load wallet', err);
    }
  }, []);
  useEffect(() => {
    loadWallet();
    fetchSettings();
    const urlParams = new URLSearchParams(window.location.search);
    const reference = urlParams.get('reference') || urlParams.get('trxref');
    if (reference) verifyPayment(reference);
  }, []);
  const fetchSettings = async () => {
    try {
      const res = await paymentService.getPublicSettings();
      if (res.success) setManualEnabled(res.data.manual_payment_enabled !== false);
    } catch (err) {
      console.error('Failed to fetch settings', err);
    }
  };
  const verifyPayment = async reference => {
    try {
      setLoading(true);
      await paymentService.verifyPayment(reference);
      toast.success('Payment verified successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
      loadTransactions();
      loadWallet();
      loadUser();
    } catch (error) {
      toast.error(error.message || 'Payment verification failed');
    } finally {
      setLoading(false);
    }
  };
  const loadTransactions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('gigship_token') || localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      const response = await paymentService.getTransactions({
        page: 1,
        limit: 50,
        category: filter === 'all' ? undefined : filter
      });
      if (response && response.data && Array.isArray(response.data.transactions)) setTransactions(response.data.transactions);else if (response && Array.isArray(response.transactions)) setTransactions(response.transactions);
    } catch (error) {
      if (error.response?.status !== 404) toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };
  const exportCSV = () => {
    if (!transactions || !transactions.length) return;
    const headers = ['Type', 'Amount', 'Status', 'Date', 'Time', 'Reference'];
    const rows = transactions.map(tx => [tx.category ? tx.category.replace('_', ' ') : '', tx.amount, tx.status, new Date(tx.createdAt).toLocaleDateString(), new Date(tx.createdAt).toLocaleTimeString(), tx.reference || '']);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "gigship-wallet-transactions.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleAddFunds = async (amount, method = 'auto', manualDetails = null) => {
    try {
      setLoading(true);
      if (method === 'manual') {
        await paymentService.submitManualDeposit({
          amount,
          reference: manualDetails.reference,
          date: manualDetails.date,
          method: 'bank_transfer'
        });
        toast.success('Manual deposit submitted for review');
        setShowAddFunds(false);
        loadTransactions();
        loadWallet();
      } else {
        const response = await paymentService.addFunds({
          amount,
          currency: 'GHS',
          email: user.email
        });
        if (response.data && response.data.authorization_url) window.location.href = response.data.authorization_url;else toast.error('Failed to initialize payment gateway');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to add funds');
    } finally {
      setLoading(false);
    }
  };
  const handleWithdraw = async (amount, details) => {
    try {
      setLoading(true);
      const methodId = details?.methodId || details?.id;
      await paymentService.withdrawFunds({
        amount: parseFloat(amount),
        methodId,
      });
      toast.success('Withdrawal request submitted!');
      setShowWithdraw(false);
      await Promise.all([loadWallet(), loadTransactions()]);
      loadUser();
    } catch (error) {
      toast.error(error.message || 'Failed to process withdrawal');
    } finally {
      setLoading(false);
    }
  };
  const getTransactionIcon = category => {
    const icons = {
      deposit: <ArrowDownLeft className="w-4 h-4 text-green-600" />,
      gig_payment: <ArrowUpRight className="w-4 h-4 text-red-600" />,
      gig_earning: <ArrowDownLeft className="w-4 h-4 text-green-600" />,
      withdrawal: <ArrowUpRight className="w-4 h-4 text-red-600" />,
      refund: <ArrowDownLeft className="w-4 h-4 text-orange-600" />
    };
    return icons[category] || <CheckCircle className="w-4 h-4 text-gray-600" />;
  };

  // Human-readable label — Gigger earnings show base GHS (no spread) to avoid confusion
  const getTransactionLabel = (category) => {
    const labels = {
      deposit: t('wallet.label_deposit', { defaultValue: 'Deposit' }),
      gig_payment: t('wallet.label_gig_payment', { defaultValue: 'Gig Payment' }),
      gig_earning: t('wallet.label_gig_earning', { defaultValue: 'Earnings (Base GHS)' }),
      withdrawal: t('wallet.label_withdrawal', { defaultValue: 'Withdrawal' }),
      refund: t('wallet.label_refund', { defaultValue: 'Refund' }),
      commission: t('wallet.label_commission', { defaultValue: 'Platform Commission' }),
      referral_bonus: t('wallet.label_referral_bonus', { defaultValue: 'Referral Bonus' }),
    };
    return labels[category] || (category ? category.replace(/_/g, ' ') : t('wallet.label_other', { defaultValue: 'Transaction' }));
  };
  const defaultMethod = paymentMethods?.find(pm => pm.isDefault) || paymentMethods?.[0];

  // ── Referral balance handlers ──────────────────────────────────────────────
  const referralBalance = wallet?.referralBalance || 0;
  const minWithdrawal = wallet?.withdrawalLimits?.minimum;
  const referralMinimum = wallet?.withdrawalLimits?.referralMinimum;
  const limitsCurrency = wallet?.withdrawalLimits?.currency || 'GHS';
  const hasReferralMinimum = typeof referralMinimum === 'number' && Number.isFinite(referralMinimum) && referralMinimum > 0;
  const isGiggerMode = user?.activeRole === 'gigger' || user?.activeRole === 'hybrid';

  // Withdrawing needs Secondary: the toast offers the two ways to get it (shared by Withdraw and the referral withdraw)
  const showSecondaryVerificationPrompt = () => {
    toast(tInstance => <div className="flex flex-col gap-2">
                            <p className="text-sm font-semibold text-gray-900">{t('wallet.secondary_verification_required', 'Verification Required')}</p>
                            <p className="text-xs text-gray-600">{t("dashboard.wallet.please_complete_verification_to_withdraw", {
                        defaultValue: "Please complete verification to withdraw earnings."
                      })}</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <button onClick={() => {
                        toast.dismiss(tInstance.id);
                        setShowIdVerifyModal(true);
                      }} className="bg-blue-600 text-white px-3 py-1.5 rounded text-xs font-bold hover:bg-blue-700">
                                {t("dashboard.wallet.upload_id_instant", {
                          defaultValue: "Upload ID (Instant)"
                        })}
                              </button>
                              <button onClick={() => {
                        toast.dismiss(tInstance.id);
                        setShowAddMethod(true);
                      }} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded text-xs font-bold hover:bg-gray-200">
                                {t("dashboard.wallet.add_payment_method", {
                          defaultValue: "Add Payment Method"
                        })}
                              </button>
                            </div>
                          </div>, {
                    icon: '🔒',
                    duration: 5000
                  });
  };

  const handleReferralWithdraw = async () => {
    const amt = parseFloat(referralAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!isGiggerMode) { toast.error('Switch to Gigger mode to withdraw your referral balance'); return; }
    if (!hasReferralMinimum) { toast.error('Withdrawal limits are still loading — please try again'); return; }
    if (amt < referralMinimum) { toast.error(`Minimum referral withdrawal is ${limitsCurrency} ${referralMinimum}`); return; }
    if (amt > referralBalance) { toast.error('Amount exceeds your referral balance'); return; }
    if (!isSecondaryVerified) { showSecondaryVerificationPrompt(); return; }
    setReferralLoading(true);
    try {
      const res = await api.post('/wallet/referral/withdraw', { amount: amt });
      const data = res.data;
      if (data?.success) {
        toast.success(`GH₵${amt.toFixed(2)} withdrawal initiated from your referral balance`);
        setReferralAction(null);
        setReferralAmount('');
        loadWallet();
        loadUser();
      } else {
        toast.error(data?.message || 'Withdrawal failed');
      }
    } catch (err) {
      if (isSecondaryVerificationError(err)) showSecondaryVerificationPrompt();
      else toast.error(err.response?.data?.message || 'Withdrawal failed');
    }
    finally { setReferralLoading(false); }
  };

  const handleReferralTransfer = async () => {
    const amt = parseFloat(referralAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setReferralLoading(true);
    try {
      const res = await api.post('/wallet/referral/transfer', { amount: amt });
      const data = res.data;
      if (data?.success) {
        toast.success(`GH₵${amt.toFixed(2)} moved to your available balance`);
        setReferralAction(null);
        setReferralAmount('');
        loadWallet();
        loadUser();
      } else {
        toast.error(data?.message || 'Transfer failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Transfer failed');
    }
    finally { setReferralLoading(false); }
  };
  return <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('wallet_title') || 'Wallet'}</h1>

        {/* Verification Alert */}
        {!isSecondaryVerified && <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-r-lg">
            <div className="flex items-start">
              <Shield className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-yellow-900">
                  {t('secondary_verification_required') || 'Secondary Verification Required'}
                </h3>
                <p className="text-sm text-yellow-700 mt-1">
                  {t("dashboard.wallet.complete_secondary_verification_to_add_f", {
                defaultValue: "Complete secondary verification to add funds or withdraw earnings. Choose one:"
              })}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button onClick={() => setShowIdVerifyModal(true)} className="inline-flex items-center text-sm font-semibold text-white hover:bg-blue-700 bg-blue-600 px-3 py-1 rounded">
                    {t("dashboard.wallet.upload_id_instant", {
                  defaultValue: "Upload ID (Instant)"
                })}
                  </button>
                  <button onClick={() => setShowAddMethod(true)} className="inline-flex items-center text-sm font-semibold text-white hover:bg-blue-700 bg-blue-600 px-3 py-1 rounded">
                    {t("dashboard.wallet.add_payment_method", {
                  defaultValue: "Add Payment Method"
                })}
                  </button>
                </div>
              </div>
            </div>
          </div>}

        <div className="grid grid-cols-1 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><Clock className="w-5 h-5 text-orange-500 mr-2" />{t('pending_clearance') || 'Pending Clearance'}</h3>
            <div className="space-y-3">
              {wallet?.pendingClearance?.length > 0 ? wallet.pendingClearance.map((item, index) => <div key={index} className="flex justify-between items-center text-sm border-b border-gray-50 pb-2 last:border-0">
                    <div><p className="font-medium text-gray-900">{t("dashboard.wallet.gh", {
                    defaultValue: "GH\u20B5"
                  })}{item.amount.toFixed(2)}</p><p className="text-gray-500 text-xs">{t("dashboard.wallet.clears", {
                    defaultValue: "Clears:"
                  })} {new Date(item.clearableAt).toLocaleDateString()}</p></div>
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">{Math.ceil((new Date(item.clearableAt) - new Date()) / (1000 * 60 * 60 * 24))} {t("dashboard.wallet.days_left", {
                  defaultValue: "days left"
                })}</span>
                  </div>) : <div className="text-center py-4 text-gray-500"><p>{t('wallet.no_pending_funds')}</p></div>}
            </div>
          </div>
        </div>



        {/* ── Referral Balance Card ─────────────────────────────────────────── */}
        {referralBalance > 0 && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg p-6 mb-4 text-white relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <ArrowDownLeft className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold">Referral Balance</h3>
                </div>
                <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">Earned from referrals</span>
              </div>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-4xl font-bold">GH₵{referralBalance.toFixed(2)}</span>
                <span className="text-blue-100 text-sm">referral earnings</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {isGiggerMode && (
                  <button
                    onClick={() => { setReferralAction('withdraw'); setReferralAmount(''); }}
                    className="px-4 py-2 bg-white text-blue-700 text-sm font-semibold rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    Withdraw
                  </button>
                )}
                <button
                  onClick={() => { setReferralAction('transfer'); setReferralAmount(''); }}
                  className="px-4 py-2 bg-white/20 text-white text-sm font-semibold rounded-lg hover:bg-white/30 transition-colors border border-white/30"
                >
                  Use as Wallet Credit
                </button>
              </div>
            </div>
            <div className="absolute top-0 right-0 -mt-8 -mr-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
          </div>
        )}

        {/* ── Referral Balance Action Panel ─────────────────────────────────── */}
        {referralAction && referralBalance > 0 && (
          <div className="bg-white border border-blue-200 rounded-xl p-5 mb-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900">
                {referralAction === 'withdraw' ? 'Withdraw Referral Balance' : 'Transfer to Available Balance'}
              </h4>
              <button onClick={() => setReferralAction(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Available referral balance: <strong className="text-gray-800">GH₵{referralBalance.toFixed(2)}</strong>
            </p>
            {referralAction === 'withdraw' && hasReferralMinimum && (
              <p className="text-xs text-gray-500 mb-3">
                Minimum referral withdrawal: {limitsCurrency} {referralMinimum}
              </p>
            )}
            <div className="flex gap-3">
              <input
                type="number" min={referralAction === 'withdraw' && hasReferralMinimum ? referralMinimum : 1} max={referralBalance} step="0.01"
                value={referralAmount}
                onChange={e => setReferralAmount(e.target.value)}
                placeholder="Enter amount…"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={referralAction === 'withdraw' ? handleReferralWithdraw : handleReferralTransfer}
                disabled={!referralAmount || referralLoading || (referralAction === 'withdraw' && !hasReferralMinimum)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {referralLoading ? 'Processing…' : referralAction === 'withdraw' ? 'Withdraw' : 'Transfer'}
              </button>
            </div>
          </div>
        )}

        {/* ── Referral & Bonus FYI Notice ───────────────────────────────────── */}
        {referralBalance > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800 text-sm mb-2">How your referral earnings work</p>
                <ul className="text-sm text-amber-700 space-y-1.5">
                  {referralBalance > 0 && (<>
                    <li>• <strong>Referral Balance</strong> is earned when someone you referred completes their first order.</li>
                    <li>• <strong>Withdraw</strong> your referral balance only in <strong>Gigger mode</strong>{hasReferralMinimum ? ` (minimum ${limitsCurrency} ${referralMinimum})` : ''}.</li>
                    <li>• In any mode, you can <strong>transfer it to your available balance</strong> to pay for orders or use as wallet credit.</li>
                  </>)}
                  {!isGiggerMode && referralBalance > 0 && (
                    <li className="font-semibold text-amber-800">⚠ You are in Shipper mode. Switch to Gigger mode in Settings to unlock cash withdrawal of your referral balance.</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Payment Methods Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('default_withdrawal_method') || 'Default Withdrawal Method'}</h3>
              <p className="text-sm text-gray-500 mt-0.5">Manage your withdrawal methods and default payout destination</p>
            </div>
            <button onClick={() => setShowAddMethod(true)} className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center">
              <Plus className="w-4 h-4 mr-1" /> {t('dashboard.wallet.add_method', { defaultValue: 'Add Method' })}
            </button>
          </div>
          <div className="p-6">
            {paymentMethods && paymentMethods.length > 0 ? (
              <div className="space-y-3">
                {paymentMethods.map(pm => {
                  const pmId = pm.id || pm._id;
                  const isDef = pm.isDefault;
                  const isVerified = pm.verificationStatus === 'verified';
                  return (
                    <div key={pmId} className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg gap-3 ${isDef ? 'border-blue-300 bg-blue-50/40' : 'bg-gray-50'}`}>
                      <div className="flex items-center">
                        <div className="p-2 bg-white rounded-lg mr-4 shadow-sm">
                          {pm.type === 'bank_transfer' ? <Building className="w-6 h-6 text-gray-600" /> : <Smartphone className="w-6 h-6 text-yellow-600" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-gray-900 capitalize">{pm.details?.bank || pm.provider}</p>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 font-medium">
                              {pm.type === 'bank_transfer' ? 'Bank Account' : pm.type === 'mobile_money' ? 'Mobile Money' : pm.type === 'card' ? 'Card' : 'Wallet'}
                            </span>
                            {isDef && (
                              <span className="text-xs px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 font-semibold flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Default
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{pm.identifier}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 self-end sm:self-center">
                        {pm.verificationStatus === 'verified' && (
                          <span className="flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" /> {t("dashboard.wallet.verified", { defaultValue: "Verified" })}
                          </span>
                        )}
                        {pm.verificationStatus === 'pending' && (
                          <span className="flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Clock className="w-3 h-3 mr-1" /> {t("dashboard.wallet.pending_review", { defaultValue: "Pending Review" })}
                          </span>
                        )}
                        {pm.verificationStatus === 'rejected' && (
                          <span className="flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <AlertCircle className="w-3 h-3 mr-1" /> {t("dashboard.wallet.rejected", { defaultValue: "Rejected" })}
                          </span>
                        )}
                        {!isDef && isVerified && (
                          <button
                            type="button"
                            onClick={() => handleSetDefault(pmId)}
                            disabled={setDefaultLoading === pmId}
                            className="text-xs px-3 py-1.5 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 font-medium transition disabled:opacity-50"
                          >
                            {setDefaultLoading === pmId ? 'Setting...' : 'Set as Default'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                <div className="mx-auto h-12 w-12 text-gray-400"><CreditCard className="h-12 w-12" /></div>
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('wallet.no_default_method')}</h3>
                <p className="mt-1 text-sm text-gray-500">{t("dashboard.wallet.add_a_bank_or_mobile_money_account_for_f", {
                  defaultValue: "Add a bank or mobile money account for faster withdrawals."
                })}</p>
                <div className="mt-6">
                  <button onClick={() => setShowAddMethod(true)} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                    <Plus className="-ml-1 mr-2 h-5 w-5" /> {t("dashboard.wallet.add_method", {
                      defaultValue: "Add Method"
                    })}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('recent_transactions') || 'Recent Transactions'}</h3>
            <div className="flex flex-wrap gap-4 w-full md:w-auto">
              <button onClick={exportCSV} className="text-sm font-medium border border-gray-300 rounded-md px-3 py-2 hover:bg-gray-50 flex items-center">
                {t('wallet.export_csv') || 'Export CSV'}
              </button>
              {/* Filter Dropdown - Floats Right/Top on mobile, or inline on desktop */}
              <select value={filter} onChange={e => setFilter(e.target.value)} className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 order-1 md:order-3 flex-grow md:flex-grow-0">
                <option value="all">{t('filter_all') || 'All Transactions'}</option>
                <option value="deposit">{t('filter_deposits') || 'Deposits'}</option>
                <option value="withdrawal">{t('filter_withdrawals') || 'Withdrawals'}</option>
                <option value="gig_payment">{t('filter_payments') || 'Payments'}</option>
                <option value="gig_earning">{t('filter_earnings') || 'Earnings'}</option>
              </select>

              {/* Action Buttons - Float below on mobile (order-2) */}
              <div className="flex space-x-2 order-2 md:order-1 flex-grow md:flex-grow-0">
                {/* Shipper Actions (Add Funds) */}
                {(user?.activeRole === 'shipper' || user?.selectedMode === 'shipper') && <button
                  id="add-funds-btn"
                  onClick={() => setShowAddFunds(true)}
                  className={`flex-1 md:flex-initial px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center whitespace-nowrap`}>
                    <Plus className="w-4 h-4 mr-2" /> {t('add_funds') || 'Add Funds'}
                  </button>}

                {/* Gigger Actions (Withdraw) */}
                {(user?.activeRole === 'gigger' || user?.selectedMode === 'gigger') && <button onClick={() => {
                if (isSecondaryVerified) {
                  setShowWithdraw(true);
                } else {
                  showSecondaryVerificationPrompt();
                }
              }} className={`flex-1 md:flex-initial px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center whitespace-nowrap`}>
                    <ArrowUpRight className="w-4 h-4 mr-2" /> {t('withdraw') || 'Withdraw'}
                  </button>}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('wallet.transaction_type') || t('type')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('amount')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('wallet.transaction_status') || t('status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('wallet.date')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('wallet.time')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('wallet.transaction_reference') || t('wallet.reference')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    </td>
                  </tr> : transactions.length === 0 ? <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">{t('wallet.no_transactions')}</td>
                  </tr> : transactions.map(tx => <tr key={tx.id || tx._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center mr-3">
                            {getTransactionIcon(tx.category)}
                          </div>
                          <div className="text-sm font-medium text-gray-900">{getTransactionLabel(tx.category)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-bold ${tx.amount > 0 ? 'text-green-600' : 'text-gray-900'}`}>
                          {tx.amount > 0 ? '+' : ''}{t("dashboard.wallet.gh", {
                      defaultValue: "GH\u20B5"
                    })}{Math.abs(tx.amount).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${tx.status === 'completed' ? 'bg-green-100 text-green-800' : tx.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                          {tx.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(tx.createdAt).toLocaleDateString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(tx.createdAt).toLocaleTimeString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{tx.reference?.substring(0, 8)}...</td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <TopUpModal
          isOpen={showAddFunds}
          onClose={() => setShowAddFunds(false)}
          onSuccess={() => { setShowAddFunds(false); loadTransactions(); loadWallet(); loadUser(); }}
          onRequirePaymentMethod={() => setShowAddMethod(true)}
        />
        {showWithdraw && (
          <WithdrawModal
            availableBalance={wallet?.availableBalance || 0}
            minWithdrawal={minWithdrawal}
            currency={limitsCurrency}
            onClose={() => setShowWithdraw(false)}
            onSubmit={handleWithdraw}
            loading={loading}
            paymentMethods={paymentMethods}
            onMethodAdded={fetchPaymentMethods}
          />
        )}
        <IdentityVerificationModal
          isOpen={showIdVerifyModal}
          onClose={() => setShowIdVerifyModal(false)}
          onSuccess={() => { setShowIdVerifyModal(false); loadWallet(); loadUser(); }}
        />
        <PaymentMethodForm
          isOpen={showAddMethod}
          onClose={() => setShowAddMethod(false)}
          onSuccess={() => {
            loadWallet();
            loadUser();
            fetchPaymentMethods();
          }}
          isInline={false}
          showSuccessStep={true}
          reloadOnSuccess={false}
        />
      </div>
    </div>;
};
export default Wallet;